import * as yup from "yup";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/ui/button";
import Common from "../../../helper/common";
import { FormikInput } from "../../../components/formik-components";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { respEnum } from "../../../constants/appConstant";
import toast from "react-hot-toast";

const FormikInitialValues = {
  baseurl: "",
  username: "",
  password: "",
  x_csrf_token: "",
};

const formikValidation = yup.object({
  baseurl: yup
    .string()
    .required("Base URL is required")
    .url("Must be a valid URL"),
  username: yup.string().required("Username is required"),
  password: yup.string().required("Password is required"),
  x_csrf_token: yup.string().nullable(),
});

const AddExternalCred = ({ id, onClose, open, setOpen }) => {
  const formikRef = useRef();
  const dialogRef = useRef();
  const [isFormikInitialized, setIsFormikInitialized] = useState(false);

  useEffect(() => {
    setIsFormikInitialized(open);
  }, [open]);

  useEffect(() => {
    if (id && open) {
      getDataInfo();
    } else if (open && !id) {
      formikRef.current?.setValues(FormikInitialValues);
    }
  }, [id, open]);

  const getDataInfo = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetExternalCreds",
        {},
        "GET",
        false
      );
      if (
        response?.status === respEnum.Success &&
        response.data &&
        response.data.length > 0
      ) {
        const creds = response.data[0];
        formikRef.current?.setValues({
          baseurl: creds.baseurl || "",
          username: creds.username || "",
          password: creds.password || "",
          x_csrf_token: creds.x_csrf_token || "",
        });
      }
    } catch (error) {
      console.error("Error fetching credentials:", error);
      toast.error("Failed to load credentials");
    }
  };

  const onSubmitData = async (d, action) => {
    try {
      let data = {
        ...d,
        id: id || null,
        club_id: global.user?.defaultClubId || null,
      };

      let response = await Common.ApiService.getInstance().request(
        "AddUpdateExternalCreds",
        data,
        "POST"
      );

      action.setSubmitting(false);

      if (response.status === respEnum.Success) {
        toast.success(response.message || "Credentials saved successfully");
        onClose?.();
        setOpen(false);
      } else {
        toast.error(response.message || "Failed to save credentials");
      }
    } catch (error) {
      action.setSubmitting(false);
      console.error("Error saving credentials:", error);
      toast.error("Failed to save credentials");
    }
  };

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            size="sm"
            variant="primary"
            className="flex items-center gap-2"
          >
            <Icon icon="heroicons:plus-20-solid" className="h-4 w-4" />
            {id ? "Edit" : "Add"} Credentials
          </Button>
        </DialogTrigger>
        <DialogContent
          ref={dialogRef}
          className="max-h-[100vh] overflow-y-auto sm:max-w-[600px]"
        >
          <DialogHeader>
            <DialogTitle className="text-base font-medium">
              {!id ? "Add" : "Update"} External ERP Credentials
            </DialogTitle>
          </DialogHeader>

          <div className="text-sm text-default-500 space-y-2">
            <Formik
              innerRef={formikRef}
              onSubmit={onSubmitData}
              initialValues={FormikInitialValues}
              validationSchema={formikValidation}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {(formikProps) => (
                <div className="flex flex-col gap-4">
                  <FormikInput
                    formikProps={formikProps}
                    name={"baseurl"}
                    label={"Base URL"}
                    placeholder={"https://example.com/api"}
                    type="url"
                  />

                  <FormikInput
                    formikProps={formikProps}
                    name={"username"}
                    label={"Username"}
                    placeholder={"Enter username"}
                  />

                  <FormikInput
                    formikProps={formikProps}
                    name={"password"}
                    label={"Password"}
                    placeholder={"Enter password"}
                    type="password"
                  />

                  <FormikInput
                    formikProps={formikProps}
                    name={"x_csrf_token"}
                    label={"X-CSRF-Token (Optional)"}
                    placeholder={"Enter CSRF token (optional)"}
                  />
                </div>
              )}
            </Formik>
          </div>

          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Close
              </Button>
            </DialogClose>
            {isFormikInitialized && formikRef.current && (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  formikRef.current?.handleSubmit();
                }}
              >
                {formikRef.current.isSubmitting
                  ? "Saving..."
                  : !id
                  ? "Add"
                  : "Update"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddExternalCred;
